/**
 * Share a Trick.jsx note via the native iOS Share Sheet (which
 * includes AirDrop as one of the transports). Progressive strategy:
 *
 *   1. Render the note to a JPEG canvas snapshot that mimics an Apple
 *      Notes screenshot, THEN inject EXIF metadata (DateTime,
 *      DateTimeOriginal, DateTimeDigitized) matching the magician's
 *      configured note-date so a spectator long-pressing the received
 *      photo -> Info sees the same backdated timestamp instead of the
 *      real "just now" moment (which would blow the trick).
 *      Wrap in a File and call `navigator.share({files})` — iOS opens
 *      its OS-level share sheet, spectator picks AirDrop → the image
 *      lands in their Photos with the spoofed metadata intact.
 *   2. Fallback: plain text share (title + body) via
 *      `navigator.share({text})`. Works on iOS 12.2+ even when
 *      files aren't accepted.
 *   3. Fallback-fallback: copy to clipboard so the magician can paste
 *      into any messaging channel manually.
 *
 * Returns an object describing which path succeeded so callers can
 * surface an appropriate toast: { ok, mode, aborted }.
 */

import piexif from "piexifjs";

// Format a Date into the EXIF ASCII "YYYY:MM:DD HH:MM:SS" convention
// using LOCAL time — matches how iOS Photos labels the Info sheet.
function formatExifDate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}:${pad(d.getMonth() + 1)}:${pad(d.getDate())} ` +
         `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// Format a Date's timezone offset for EXIF OffsetTime tags (v2.31+).
// Result: "+02:00" / "-05:00". iOS Photos uses this to show local time.
function formatExifOffset(d) {
  const total = -d.getTimezoneOffset(); // minutes east of UTC
  const sign = total >= 0 ? "+" : "-";
  const abs = Math.abs(total);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `${sign}${hh}:${mm}`;
}

// Build a piexif-compatible EXIF dictionary that stamps `d` as the
// photo's capture moment. Kept minimal — only the tags Photos actually
// surfaces on the Info sheet, so we don't confuse EXIF parsers with
// half-filled optional fields.
function buildExifBytes(d) {
  const exifDate = formatExifDate(d);
  const offset = formatExifOffset(d);
  const zeroth = {
    [piexif.ImageIFD.DateTime]: exifDate,
    // Software tag — some viewers show it on Info; a benign value
    // that reads like a real camera makes the metadata less suspect.
    [piexif.ImageIFD.Software]: "Notes",
  };
  const exif = {
    [piexif.ExifIFD.DateTimeOriginal]: exifDate,
    [piexif.ExifIFD.DateTimeDigitized]: exifDate,
    [piexif.ExifIFD.OffsetTime]: offset,
    [piexif.ExifIFD.OffsetTimeOriginal]: offset,
    [piexif.ExifIFD.OffsetTimeDigitized]: offset,
  };
  return piexif.dump({ "0th": zeroth, Exif: exif });
}

// Wrap a string to `maxWidth` pixels using the current font on `ctx`.
// Preserves manual newlines and treats a run of spaces as a single
// separator (avoids leading/trailing space artefacts).
function wrapText(ctx, text, maxWidth) {
  const source = String(text ?? "");
  const paragraphs = source.split("\n");
  const lines = [];
  for (const paragraph of paragraphs) {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (words.length === 0) { lines.push(""); continue; }
    let current = "";
    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
  }
  return lines;
}

// Render an Apple-Notes-styled snapshot of {title, date, body} to a
// JPEG File with EXIF metadata backdated to `photoDate`. Returns null
// if canvas/blob generation fails so the caller can gracefully fall
// back to text sharing.
async function renderNoteToImageFile({ title, date, body, photoDate }) {
  try {
    // iPhone-portrait-ish canvas — 2x pixel density for crisp AirDrop
    // preview on retina screens without ballooning file size.
    const width = 828;
    const paddingX = 56;
    const paddingTop = 96;
    const paddingBottom = 96;
    const titleSize = 44;
    const titleLineH = 54;
    const dateSize = 22;
    const dateGap = 20;
    const bodySize = 30;
    const bodyLineH = 44;
    const bodyGap = 40;

    // Measurement pass — we need to know how tall the canvas must be
    // BEFORE drawing (canvas element can't grow retroactively).
    const measureCanvas = document.createElement("canvas");
    const mctx = measureCanvas.getContext("2d");
    const titleFont = `700 ${titleSize}px -apple-system, "SF Pro Text", "Helvetica Neue", Arial, sans-serif`;
    const bodyFont  = `400 ${bodySize}px -apple-system, "SF Pro Text", "Helvetica Neue", Arial, sans-serif`;

    mctx.font = titleFont;
    const titleLines = wrapText(mctx, title || "", width - paddingX * 2);

    mctx.font = bodyFont;
    const bodyLines = wrapText(mctx, body || "", width - paddingX * 2);

    const contentHeight =
      titleLines.length * titleLineH +
      dateGap + dateSize +
      bodyGap +
      Math.max(bodyLines.length, 1) * bodyLineH;

    const height = Math.max(1200, paddingTop + contentHeight + paddingBottom);

    // Draw pass.
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    // White page — matches Apple Notes light mode. Dark mode support
    // could sniff prefers-color-scheme in future.
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, width, height);

    ctx.textBaseline = "top";

    let y = paddingTop;

    // Title — heavy weight black.
    ctx.fillStyle = "#000000";
    ctx.font = titleFont;
    for (const line of titleLines) {
      ctx.fillText(line, paddingX, y);
      y += titleLineH;
    }

    // Small vertical breath before the date.
    y += dateGap;

    // Date — dim gray, matches Apple Notes secondary label.
    ctx.fillStyle = "#8E8E93";
    ctx.font = `400 ${dateSize}px -apple-system, "SF Pro Text", "Helvetica Neue", Arial, sans-serif`;
    ctx.fillText(date || "", paddingX, y);
    y += dateSize + bodyGap;

    // Body — regular black.
    ctx.fillStyle = "#000000";
    ctx.font = bodyFont;
    for (const line of bodyLines) {
      ctx.fillText(line, paddingX, y);
      y += bodyLineH;
    }

    // JPEG so we can attach EXIF (PNG's tEXt chunks are ignored by
    // iOS Photos' Info sheet). Quality 0.92 is visually lossless for
    // the flat white + text content while keeping file size small.
    const jpegBlob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92),
    );
    if (!jpegBlob) return null;

    // Splice EXIF into the JPEG bytes. Read as a binary string
    // (piexifjs API) then re-wrap into a Blob for File construction.
    let finalBlob = jpegBlob;
    if (photoDate instanceof Date && !Number.isNaN(photoDate.getTime())) {
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload  = () => resolve(r.result);
          r.onerror = () => reject(r.error);
          r.readAsDataURL(jpegBlob);
        });
        const exifBytes = buildExifBytes(photoDate);
        const injected  = piexif.insert(exifBytes, dataUrl);
        // Convert the injected base64 dataURL back to a Blob without
        // atob/btoa gymnastics — fetch handles the decode natively.
        finalBlob = await (await fetch(injected)).blob();
      } catch {
        // EXIF injection is best-effort — if it fails we still share
        // the plain JPEG rather than degrade to a text-only share.
        finalBlob = jpegBlob;
      }
    }

    const safeName =
      (title || "note")
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 40) || "note";
    return new File([finalBlob], `${safeName}.jpg`, { type: "image/jpeg" });
  } catch {
    return null;
  }
}

export async function shareNoteAsAirDrop({ title, date, body, photoDate }) {
  const cleanTitle = String(title || "").trim();
  const cleanBody  = String(body  || "").trim();
  const combinedText = cleanTitle
    ? `${cleanTitle}\n\n${cleanBody}`
    : cleanBody;

  // --- 1. Image + text share (best UX) --------------------------------
  try {
    const file = await renderNoteToImageFile({
      title: cleanTitle,
      date,
      body: cleanBody,
      photoDate,
    });
    if (
      file &&
      typeof navigator !== "undefined" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files: [file] })
    ) {
      await navigator.share({
        files: [file],
        title: cleanTitle || "Note",
        text: combinedText,
      });
      return { ok: true, mode: "image" };
    }
  } catch (err) {
    if (err && err.name === "AbortError") {
      return { ok: false, aborted: true, mode: "image" };
    }
    // Fall through to text share.
  }

  // --- 2. Text-only share --------------------------------------------
  try {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      await navigator.share({
        title: cleanTitle || "Note",
        text: combinedText,
      });
      return { ok: true, mode: "text" };
    }
  } catch (err) {
    if (err && err.name === "AbortError") {
      return { ok: false, aborted: true, mode: "text" };
    }
    // Fall through to clipboard.
  }

  // --- 3. Clipboard fallback -----------------------------------------
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      await navigator.clipboard.writeText(combinedText);
      return { ok: true, mode: "clipboard" };
    }
  } catch { /* nothing else we can do */ }

  return { ok: false };
}
